import React from 'react';
import { Box, VStack, createListCollection, Center, Icon } from '@chakra-ui/react';
import { TbTruck } from 'react-icons/tb';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery, useGetMailSettingsQuery } from '../../api';
import { formatAddress } from '../../utils/addressUtils';
import Button from '../../components/base/Button';
import { InfoSection } from '../../components/profile/InfoSection';
import { ForwardingPeriodicity } from '../../api/mails/types';
import LoadingState from '../../components/base/LoadingState';
import SettingSwitchRow from '../../components/mail/SettingSwitchRow';
import SettingSelectRow from '../../components/mail/SettingSelectRow';
import PageHeader from '../../components/base/PageHeader';
import { useMailsSettings } from '../../hooks/useMailsSettings';
import { InfoItem } from '../../components/profile/InfoItem';
import { FiMapPin, FiEdit } from 'react-icons/fi';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import SingleForwardingStatus from '../../components/mail/SingleForwardingStatus';

const ForwardingPage: React.FC = () => {
    const { t } = useTranslation();
    const { data: customer, isLoading: isCustomerLoading } = useGetCustomerQuery();
    const { data: mailSettingsData, isLoading: isMailsLoading } = useGetMailSettingsQuery();

    // Используем хук для управления настройками почты
    const { settings, updateSettings, isLoading: isSettingsLoading, isSaving } = useMailsSettings();
    const { sendEmail } = useSupportEmail();

    const forwardingPeriodicity = settings?.forwarding_periodicity;
    const isRegisteredMail = settings?.forwarding_type === 'ordered';

    const singleForwarding = mailSettingsData?.single_forwarding;

    const forwardingOptions = createListCollection({
        items: [
            { value: 'off', label: t.pages.mail.forwardNever },
            { value: 'month', label: t.pages.mail.forwardOncePerMonth },
            { value: 'two_weeks', label: t.pages.mail.forwardTwicePerMonth },
        ],
    });

    if (isCustomerLoading || !customer || isMailsLoading) {
        return <LoadingState message={t.pages.support.loading} />;
    }

    return (
        <Box
            h="calc(100vh - 20px)"
            bg="gray.100"
            px={4}
            pt={2}
            maxW="4xl"
            mx="auto"
            pb={{ base: 20, md: 6 }}
            overflow="auto"
        >
            {/* Заголовок */}
            <PageHeader title={t.pages.mail.forwarding} />

            <VStack gap={6} align="stretch" maxW={{ base: 'full', md: '600px' }} mx="auto">
                {/* Контент настроек */}
                <VStack gap={6} align="stretch">
                    {/* Информационный блок с адресом */}
                    <InfoSection separator={false}>
                        <Center mb={2}>
                            <Icon as={TbTruck} boxSize={12} color="yellow.600" />
                        </Center>
                        <InfoItem
                            icon={FiMapPin}
                            label={t.pages.profile.mailingAddress}
                            value={formatAddress(customer?.personal_address) || '-'}
                            stacked={true}
                        />
                        <Button
                            buttonType="success"
                            w="full"
                            onClick={() => sendEmail(t.pages.support.editDataEmailSubject)}
                        >
                            <Icon as={FiEdit} boxSize={4} />
                            {t.pages.mail.changeAddressButton}
                        </Button>
                    </InfoSection>

                    {/* Раздел 1: Настройки регулярной пересылки */}
                    <InfoSection
                        title={t.pages.mail.regularForwarding}
                        separator={false}
                        isLoading={isSettingsLoading}
                        loadingLabel={t.pages.support.loading}
                        isUpdate={isSaving}
                    >
                        <SettingSelectRow
                            label={t.pages.mail.forwardMail}
                            value={forwardingPeriodicity}
                            collection={forwardingOptions}
                            onValueChange={(value) =>
                                updateSettings({ forwarding_periodicity: value as ForwardingPeriodicity })
                            }
                            minW="160px"
                            maxW="220px"
                            disabled={isSaving}
                        />

                        {/* Строка 2: Заказное письмо */}
                        <SettingSwitchRow
                            label={t.pages.mail.sendRegisteredMail}
                            checked={isRegisteredMail}
                            onCheckedChange={(checked) =>
                                updateSettings({
                                    forwarding_type: checked ? 'ordered' : 'ordinary',
                                })
                            }
                            disabled={forwardingPeriodicity === 'off' || isSaving}
                        />
                    </InfoSection>

                    {/* Раздел 2: Заказать единоразовую пересылку */}
                    <InfoSection title={t.pages.mail.oneTimeForwarding}>
                        <SingleForwardingStatus singleForwarding={singleForwarding} />
                    </InfoSection>
                </VStack>
            </VStack>
        </Box>
    );
};

export default ForwardingPage;
